import * as Sentry from '@sentry/react';
import type { ValidationContext, ValidationIssue } from '@sleeved/lib';

/**
 * D123a monitor-mode reporter for `packages/overlay`. Injected into
 * `createBoundaryValidator` at the overlay's WS ingress (Phase 4 wiring).
 *
 * Existing Sentry (`sentryInit.ts`) ONLY — deliberately no `console.warn`.
 * The overlay runs unattended inside OBS as a browser source; console logs
 * are never seen by anyone, so a console-only fallback would be silent noise
 * with no observer.
 *
 * Emits ONLY the seam's already-projected `{path, code}` issues — never a raw
 * ZodError, `.message`, `received`/`input`, or any payload VALUE.
 *
 * Sentry tag contract (binding, spec §2): the emitted TAG KEY is `schema`
 * (not `schemaName`) — `ctx.schemaName` is the internal field name; this
 * function maps it to the `schema` tag dashboards/queries key on.
 */
export function reportSchemaValidationMismatch(
  issues: ValidationIssue[],
  ctx: ValidationContext,
): void {
  Sentry.captureMessage('Schema validation mismatch', {
    level: 'warning',
    tags: {
      surface: 'schema-validation',
      schema: ctx.schemaName,
      boundary: ctx.boundary,
    },
    fingerprint: ['schema-validation', ctx.boundary, ctx.schemaName],
    extra: {
      issues,
      ...(ctx.frame ? { frame: ctx.frame } : {}),
    },
  });
}
